import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "list", "count", "countBadge",
    "settingsSection", "settingsToggle", "settingsForm", "roomNameInput", "saveBtn",
    "addMemberBtn", "addMemberPanel", "excludedList"
  ]
  static values = {
    roomId: String,
    roomTitle: String,
    membersUrl: String,
    updateUrl: String,
    leaveUrl: String,
    joinUrl: String,
    excludedUrl: String,
    familyInvitationsUrl: String,
    isOwner: Boolean,
    canManage: Boolean,
    currentUserId: String,
    confirmLeave: String,
    confirmRemove: String,
    settingsLabel: String,
    roomNameLabel: String,
    roomNameHint: String,
    saveLabel: String,
    savingLabel: String,
    savedLabel: String,
    addMemberLabel: String,
    excludedMembersLabel: String,
    noExcludedMembers: String,
    reinviteLabel: String,
    inviteNewLabel: String
  }

  connect() {
    this.loadMembers()
  }

  // Toggle panel visibility (mobile)
  toggle() {
    this.element.classList.toggle("open")
  }

  // Toggle settings form
  toggleSettings() {
    if (!this.hasSettingsFormTarget) return

    const isOpen = this.settingsFormTarget.classList.contains("open")
    this.settingsFormTarget.classList.toggle("open")
    this.settingsToggleTarget.classList.toggle("open")

    if (!isOpen && this.hasRoomNameInputTarget) {
      this.roomNameInputTarget.focus()
    }
  }

  // Save settings (room name)
  async saveSettings() {
    if (!this.hasRoomNameInputTarget || !this.hasSaveBtnTarget) return

    const newTitle = this.roomNameInputTarget.value.trim()
    const originalText = this.saveBtnTarget.textContent

    this.saveBtnTarget.disabled = true
    this.saveBtnTarget.textContent = this.savingLabelValue || "Saving..."

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({ room: { title: newTitle } })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error?.message || "Failed to update")
      }

      // Update page title
      const navTitle = document.querySelector(".chat-nav .nav-title")
      if (navTitle) {
        navTitle.textContent = newTitle || this.getDefaultTitle()
      }

      // Show success state briefly
      this.saveBtnTarget.textContent = this.savedLabelValue || "Saved!"
      setTimeout(() => {
        this.saveBtnTarget.textContent = originalText
        this.saveBtnTarget.disabled = false
      }, 1500)

    } catch (error) {
      console.error("Failed to save settings:", error)
      alert(error.message)
      this.saveBtnTarget.textContent = originalText
      this.saveBtnTarget.disabled = false
    }
  }

  // Toggle add member panel
  toggleAddMember() {
    if (!this.hasAddMemberPanelTarget) return

    const isOpen = this.addMemberPanelTarget.classList.contains("open")
    this.addMemberPanelTarget.classList.toggle("open")

    if (!isOpen) {
      this.loadExcludedMembers()
    }
  }

  // Load excluded members
  async loadExcludedMembers() {
    if (!this.hasExcludedListTarget || !this.hasExcludedUrlValue) return

    try {
      const response = await fetch(this.excludedUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const members = await response.json()
      this.renderExcludedMembers(members)
    } catch (error) {
      console.error("Failed to load excluded members:", error)
      this.excludedListTarget.innerHTML = `<p class="no-excluded-members">${this.noExcludedMembersValue || "No excluded members"}</p>`
    }
  }

  renderExcludedMembers(members) {
    if (!this.hasExcludedListTarget) return

    if (members.length === 0) {
      this.excludedListTarget.innerHTML = `<p class="no-excluded-members">${this.noExcludedMembersValue || "No excluded members"}</p>`
      return
    }

    const html = members.map(member => `
      <div class="excluded-member-item" data-user-id="${member.user_id}">
        <div class="excluded-member-avatar">
          <img src="${member.avatar?.small || ''}" alt="${this.escapeHtml(member.nickname)}" loading="lazy">
        </div>
        <span class="excluded-member-name">${this.escapeHtml(member.nickname)}</span>
        <button type="button" class="btn-reinvite" data-action="click->room-members#reinviteMember" data-user-id="${member.user_id}">
          ${this.reinviteLabelValue || "Reinvite"}
        </button>
      </div>
    `).join("")

    this.excludedListTarget.innerHTML = html
  }

  // Reinvite a member
  async reinviteMember(event) {
    const button = event.currentTarget
    const userId = button.dataset.userId
    const originalText = button.textContent

    button.disabled = true
    button.textContent = "..."

    try {
      const response = await fetch(`${this.membersUrlValue}/${userId}/reinvite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error?.message || "Failed to reinvite")
      }

      // Remove from excluded list
      const item = this.excludedListTarget.querySelector(`[data-user-id="${userId}"]`)
      if (item) {
        item.remove()
      }

      // Check if list is now empty
      if (this.excludedListTarget.querySelectorAll(".excluded-member-item").length === 0) {
        this.excludedListTarget.innerHTML = `<p class="no-excluded-members">${this.noExcludedMembersValue || "No excluded members"}</p>`
      }

      // Reload members list
      this.loadMembers()
    } catch (error) {
      console.error("Failed to reinvite member:", error)
      alert(error.message)
      button.textContent = originalText
      button.disabled = false
    }
  }

  // Load members from API
  async loadMembers() {
    if (!this.hasMembersUrlValue) return

    try {
      const response = await fetch(this.membersUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const members = await response.json()
      this.renderMembers(members)
      this.updateCount(members.length)
    } catch (error) {
      console.error("Failed to load members:", error)
      this.renderError()
    }
  }

  // Render member list
  renderMembers(members) {
    if (!this.hasListTarget) return

    const html = members.map(member => this.renderMemberItem(member)).join("")
    this.listTarget.innerHTML = html
  }

  renderMemberItem(member) {
    const isCurrentUser = member.user_id === this.currentUserIdValue
    const canRemove = this.canManageValue && !member.is_owner && !isCurrentUser

    const roleLabel = member.is_owner ? this.getOwnerLabel() :
                      member.role === "admin" ? this.getAdminLabel() : ""

    return `
      <div class="member-item ${isCurrentUser ? 'is-current-user' : ''}" data-user-id="${member.user_id}">
        <div class="member-avatar">
          <img src="${member.avatar?.small || ''}" alt="${this.escapeHtml(member.nickname)}" loading="lazy">
        </div>
        <div class="member-info">
          <span class="member-name">${this.escapeHtml(member.nickname)}</span>
          ${roleLabel ? `<span class="member-role ${member.is_owner ? 'owner' : 'admin'}">${roleLabel}</span>` : ''}
        </div>
        ${canRemove ? `
          <button type="button" class="member-remove" data-action="click->room-members#removeMember" data-user-id="${member.user_id}" data-nickname="${this.escapeHtml(member.nickname)}" title="移除成员">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        ` : ''}
      </div>
    `
  }

  renderError() {
    if (!this.hasListTarget) return
    this.listTarget.innerHTML = `
      <div class="members-error">
        <p>Failed to load members</p>
        <button type="button" class="btn-retry" data-action="click->room-members#loadMembers">Retry</button>
      </div>
    `
  }

  updateCount(count) {
    if (this.hasCountTarget) {
      this.countTarget.textContent = count
    }
    if (this.hasCountBadgeTarget) {
      this.countBadgeTarget.textContent = count
    }
  }

  // Leave the room
  async leave() {
    const confirmMessage = this.confirmLeaveValue || "Are you sure you want to leave this chat?"
    if (!confirm(confirmMessage)) return

    try {
      const response = await fetch(this.leaveUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error?.message || "Failed to leave")
      }

      // Redirect to chat list
      window.location.href = "/chat"
    } catch (error) {
      console.error("Failed to leave room:", error)
      alert(error.message)
    }
  }

  // Remove a member (admin only)
  async removeMember(event) {
    const button = event.currentTarget
    const userId = button.dataset.userId
    const nickname = button.dataset.nickname

    const confirmMessage = (this.confirmRemoveValue || "Are you sure you want to remove %{name}?").replace("%{name}", nickname)
    if (!confirm(confirmMessage)) return

    try {
      const response = await fetch(`${this.membersUrlValue}/${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error?.message || "Failed to remove member")
      }

      // Remove from list and update count
      const memberItem = this.listTarget.querySelector(`[data-user-id="${userId}"]`)
      if (memberItem) {
        memberItem.remove()
        const currentCount = parseInt(this.countTarget?.textContent || "0", 10)
        this.updateCount(Math.max(0, currentCount - 1))
      }
    } catch (error) {
      console.error("Failed to remove member:", error)
      alert(error.message)
    }
  }

  // Helper methods
  getCsrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  getOwnerLabel() {
    return document.documentElement.lang === "zh" ? "群主" : "Owner"
  }

  getAdminLabel() {
    return document.documentElement.lang === "zh" ? "管理员" : "Admin"
  }

  getDefaultTitle() {
    return document.documentElement.lang === "zh" ? "家庭群聊" : "Family Chat"
  }
}
